import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useTranslation } from "../translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail, fallback) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join(", ");
  return fallback;
};

export const ForgotPasswordDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleOpenChange = (v) => {
    if (!v) {
      setStep("email");
      setCode("");
      setNewPassword("");
      setConfirm("");
      setError("");
    }
    onOpenChange(v);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/forgot-password`, { email });
      setStep("reset");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail, t("errorUnknown")));
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError(t("passwordMinLength"));
      return;
    }
    if (newPassword !== confirm) {
      setError(t("passwordsDontMatch"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reset-password`, { email, code, new_password: newPassword });
      toast.success(t("passwordResetSuccess"));
      handleOpenChange(false);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail, t("errorUnknown")));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>{t("forgotPasswordTitle")}</DialogTitle>
          <DialogDescription>
            {step === "email" ? t("forgotPasswordDesc") : t("codeSentInfo")}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <Label htmlFor="fp-email">{t("email")}</Label>
              <Input
                id="fp-email"
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="forgot-send-code-btn" type="submit" className="w-full" disabled={loading}>
              {loading ? t("sending") : t("sendCode")}
            </Button>
            <button
              type="button"
              data-testid="already-have-code-link"
              onClick={() => { setError(""); setStep("reset"); }}
              className="w-full text-center text-sm text-green-700 hover:underline"
            >
              {t("alreadyHaveCode")}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <Label htmlFor="fp-email2">{t("email")}</Label>
              <Input
                id="fp-email2"
                data-testid="reset-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="fp-code">{t("verificationCode")}</Label>
              <Input
                id="fp-code"
                data-testid="reset-code-input"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                className="tracking-[0.5em] text-center font-bold text-lg"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            <div>
              <Label htmlFor="fp-new">{t("newPasswordLabel")}</Label>
              <Input
                id="fp-new"
                data-testid="reset-new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="fp-confirm">{t("confirmPasswordLabel")}</Label>
              <Input
                id="fp-confirm"
                data-testid="reset-confirm-password-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="reset-submit-btn" type="submit" className="w-full" disabled={loading}>
              {loading ? t("resetting") : t("resetPasswordBtn")}
            </Button>
            <button
              type="button"
              data-testid="back-to-email-step"
              onClick={() => { setError(""); setStep("email"); }}
              className="w-full text-center text-sm text-green-700 hover:underline"
            >
              {t("requestNewEmail")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
